import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { helperService } from "../../services/helper.service";
import { useMessages } from "../../context/MessagesContext";

import UserMenu from "../../components/UserMenu/UserMenu";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import Icon from "../Icon/Icon";
import Button from "../Button/Button";
import { Drawer, useDrawer, IMenuItem } from "src/components/Drawer/Drawer";

import styles from "../Messages/Messages.module.scss";

import logo from "../../assets/logo.svg";
import logoWhite from "../../assets/logo_white.svg";
import mobileLogo from "../../assets/eagerdog.svg";
import mobileLogoWhite from "../../assets/eagerdog_white.svg";

import "./Header.scss";

interface IProps {

}

const Header: React.FC<IProps> = (IProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = helperService.isLoggedIn();
  const user = helperService.getUser();
  const club = helperService.getClub();
  const { unreadCount, isOpen, openMessages, closeMessages } = useMessages();

  const { toggle, isShown } = useDrawer();

  const isLoginPage = location.pathname === "/login";
  // The transparent-over-hero header treatment only makes sense on the
  // logged-out marketplace hero. Logged-in Home has no hero image to sit on
  // top of, so it gets the normal solid header like every other page.
  const isDiscoverPage = location.pathname === "/" && !isLoggedIn;

  const isLoggedOutOnLogin = isLoginPage && !isLoggedIn;
  const isClubManager = user.user_type === Constants.user_type.club_manager;
  const hasActiveSubscription = !DOG_OWNER_PREMIUM_ENABLED || (user.has_dog_profile_access ?? false);
  const clubHasActiveSubscription = club._id && (club.has_email_contacts_access || club.has_export_contacts_access);
  const isDogOwner = user.user_type === Constants.user_type.owner;

  // const showPremiumCta = !isLoginPage && (
  //   (isDogOwner && !hasActiveSubscription) || 
  //   (isClubManager && club._id && !clubHasActiveSubscription)
  // );

  const logoLink = isClubManager && club._id ? `/k/${club._id}` : "/";

  const menuItems:IMenuItem[] = [
    ...(!isDiscoverPage ? [{
      label: "Home",
      url: "/",
      icon: "home"
    }] : []), {
      label: "Events",
      url: "/events",
      icon: "events"
    }, {
      label: "Courses & Classes",
      url: "/courses",
      icon: "school"
    }, {
      label: "Clubs",
      url: "/kennel-clubs",
      icon: "group"
    }
  ];

  const showEnterAnEvent = isLoggedIn && user.user_type !== Constants.user_type.club_manager;

  const [scrollY, setScrollY] = useState(0);
  const scrollBreakpoint:number = 20;

  const handleScroll = () => {
    setScrollY(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navLinks = (
    <>
      {!isDiscoverPage && <Link to="/" className={location.pathname === "/" ? "active" : ""}>Home</Link>}
      <Link to="/events" className={location.pathname === "/events" ? "active" : ""}>Events</Link>
      <Link to="/courses" className={location.pathname === "/courses" ? "active" : ""}>Courses & Classes</Link>
      <Link to="/kennel-clubs" className={location.pathname === "/kennel-clubs" ? "active" : ""}>Clubs</Link>
    </>
  );

  const notificationBell = isLoggedIn && (
    <div className={styles.notificationBell} onClick={isOpen ? closeMessages : openMessages} title="Messages">
      <Icon
        type="notification"
        className={[
          styles.notificationIcon,
          isDiscoverPage && scrollY <= scrollBreakpoint ? styles.onOrangeHeader : '',
          isDiscoverPage && scrollY > scrollBreakpoint ? styles.whiteout : '',
        ].filter(Boolean).join(' ')}
      />
      {unreadCount > 0 && (
        <span className={styles.unreadBadge}>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </div>
  );

  if (isDiscoverPage || isLoginPage) {
    return (
      <div className={`Header ${isLoggedOutOnLogin ? ' login-page-mobile' : ''} ${isDiscoverPage ? 'discover' + (scrollY > scrollBreakpoint ? " whiteout" : "") : ''}`}>
        <div className="headerInner">
          <div onClick={() => { toggle(); }} className="mobile-menu">
            <Icon type="menu" className="mobile-icon" />
          </div>
          <div className="logo">
            <Link to={logoLink}>
              <img src={scrollY > scrollBreakpoint ? logo : logoWhite} alt="Eager Dog" className="desktop-logo"/>
              <img src={scrollY > scrollBreakpoint ? mobileLogo : mobileLogoWhite} alt="Eager Dog" className="mobile-logo"/>
            </Link>
          </div>

          {!isLoginPage && <div className="navigation no-select">{navLinks}</div>}

          {showEnterAnEvent && (
            <Button className="enterEventCta no-select" onClick={() => { navigate("/events"); }}>
              Enter an Event
            </Button>
          )}

          <div className="user">
            {notificationBell}
            <UserMenu
              className={isDiscoverPage ? scrollY > scrollBreakpoint ? "discover whiteout" : "discover" : ""}
            />
          </div>
          <Drawer isShown={isShown} hide={toggle} menuItems={menuItems} />
        </div>
      </div>
    );
  }

  // Stacked header is the same at every width: logo, inline search pill,
  // actions, then the pill-nav row (127px total). No drawer on mobile.
  return (
    <div className="Header stacked">
      <div className="headerTop">
        <div className="logo">
          <Link to={logoLink}>
            <img src={logo} alt="Eager Dog" className="desktop-logo"/>
            <img src={mobileLogo} alt="Eager Dog" className="mobile-logo"/>
          </Link>
        </div>

        <GlobalSearch className="headerSearch" />

        <div className="headerActions">
          {showEnterAnEvent && (
            <Button className="enterEventCta no-select" onClick={() => { navigate("/events"); }}>
              Enter an Event
            </Button>
          )}
          {notificationBell}
          <UserMenu />
        </div>
      </div>

      <div className="headerTabs no-select">{navLinks}</div>
    </div>
  );
};

export default Header;
